import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '@chainlit/react-client';
import { CheckCircle2 } from 'lucide-react';

import { ChainlitContext } from 'client-types/*';

import Alert from '@/components/Alert';
import AuthLayout from '@/components/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFetch } from '@/hooks/useFetch';

interface FormValues {
  name: string;
  email: string;
  phone: string;
  organization: string;
  subject: string;
  message: string;
}

export default function Support() {
  const apiClient = useContext(ChainlitContext) as any;
  const { isAuthenticated } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailUrl = isAuthenticated ? apiClient.buildEndpoint('/user/email') : null;
  const { data: emailData } = useFetch(emailUrl);
  const sessionEmail =
    typeof emailData?.email === 'string' && emailData.email ? emailData.email : undefined;
  const emailLocked = isAuthenticated && !!sessionEmail;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      organization: '',
      subject: '',
      message: ''
    }
  });

  useEffect(() => {
    if (sessionEmail) {
      setValue('email', sessionEmail);
    }
  }, [sessionEmail, setValue]);

  const onSubmit = async (data: FormValues) => {
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.submitSupportRequest({
        name: data.name.trim() || undefined,
        email: (emailLocked ? sessionEmail! : data.email).trim().toLowerCase(),
        phone: data.phone.trim() || undefined,
        organization: data.organization.trim() || undefined,
        subject: data.subject.trim(),
        message: data.message.trim()
      });
      setSubmitted(true);
    } catch (err: any) {
      if (err?.status === 429) {
        setError('Too many requests. Please try again in a bit.');
      } else if (err?.status === 501) {
        setError('Support is not configured for this deployment yet.');
      } else {
        setError(err?.message || 'Could not submit your request. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backLink = (
    <Link
      to={isAuthenticated ? '/' : '/login'}
      className="font-medium text-primary underline-offset-4 hover:underline"
    >
      {isAuthenticated ? 'Back to Crimson Raven' : 'Back to sign in'}
    </Link>
  );

  return (
    <AuthLayout subtitle="Support">
      {submitted ? (
        <div className="text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-4 text-2xl font-bold">Request received</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Thanks for reaching out. Our team will follow up by email.
          </p>
          <p className="mt-6">{backLink}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Contact support</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Send us a message and we'll get back to you by email.
            </p>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <div className="grid gap-2">
            <Label htmlFor="name">Name (optional)</Label>
            <Input
              id="name"
              autoFocus
              disabled={submitting}
              placeholder="Your name"
              {...register('name')}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              disabled={submitting || emailLocked}
              readOnly={emailLocked}
              placeholder="you@organization.com"
              {...register('email', {
                required: emailLocked ? false : 'Email is required',
                pattern: emailLocked
                  ? undefined
                  : {
                      value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                      message: 'Enter a valid email address'
                    }
              })}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              disabled={submitting}
              placeholder="Your phone number"
              {...register('phone')}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="organization">Organization (optional)</Label>
            <Input
              id="organization"
              disabled={submitting}
              placeholder="Your company or organization"
              {...register('organization')}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              disabled={submitting}
              placeholder="What's this about?"
              {...register('subject', {
                required: 'Subject is required',
                maxLength: { value: 200, message: 'Subject must be 200 characters or fewer' }
              })}
            />
            {errors.subject && (
              <p className="text-sm text-destructive">{errors.subject.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="message">Message</Label>
            <textarea
              id="message"
              rows={5}
              disabled={submitting}
              placeholder="Describe the issue or question"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              {...register('message', {
                required: 'A message is required',
                maxLength: { value: 5000, message: 'Keep this under 5000 characters' }
              })}
            />
            {errors.message && (
              <p className="text-sm text-destructive">{errors.message.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send message'}
          </Button>

          <p className="text-center text-sm text-muted-foreground">{backLink}</p>
        </form>
      )}
    </AuthLayout>
  );
}
